import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
/* eslint-disable no-unused-vars */
import { toggleCollaborator, toggleSeller, toggleAdmin, toggleInstructor } from '../../services/firestore';
import toast from 'react-hot-toast';
import {
    FiX, FiLoader, FiUsers, FiShield, FiSettings, FiSearch,
    FiUserCheck, FiUserX, FiTrash2, FiAlertTriangle, FiChevronDown,
    FiLock, FiActivity, FiDatabase, FiMail, FiCalendar, FiClock,
    FiStar, FiRefreshCw, FiDroplet, FiBriefcase, FiGlobe, FiPhone,
    FiCheck, FiXCircle, FiPause, FiKey, FiEye, FiEyeOff, FiSave
} from 'react-icons/fi';
import { subscribeToWorkspaces, approveWorkspace, rejectWorkspace, suspendWorkspace } from '../../services/workspaceService';
import { saveGeminiKey, subscribeToGeminiKeyMeta } from '../../services/appConfig';
import { callGemini } from '../../services/gemini';
import { useTheme, COLOR_PRESETS, SECTIONS } from '../../contexts/ThemeContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const ADMIN_EMAIL = 'vcecursos@gmail.com';

// ═══════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════
// App modules for permissions
const APP_MODULES = [
    // hidden: módulo desactivado (MODULOS_OCULTOS en App.js); no se ofrece en permisos.
    { key: 'gestorPro', label: 'Gestor Pro', icon: '📋', hidden: true },
    { key: 'generador', label: 'Generador', icon: '📝', hidden: true },
    { key: 'tareas', label: 'Tareas', icon: '✅' },
    { key: 'agenteIA', label: 'Agente IA', icon: '🤖', hidden: true },
    { key: 'calendario', label: 'Calendario', icon: '📅' },
    { key: 'horasExtra', label: 'Horas Extra', icon: '⏱️' },
    { key: 'ventas', label: 'Ventas', icon: '💰' },
    // Opt-in (apagado por defecto): da acceso a la vista de calendario del instructor.
    { key: 'calInstructor', label: 'Cal. Instructor', icon: '🧑‍🏫', optIn: true },
];

// Beautiful Confirm Modal
const ConfirmModal = ({ isOpen, title, message, userName, onConfirm, onCancel, type = 'danger' }) => {
    if (!isOpen) return null;
    const colors = type === 'danger'
        ? { bg: 'bg-red-50', icon: 'bg-red-100 text-red-600', btn: 'bg-red-600 hover:bg-red-700', ring: 'ring-red-100' }
        : { bg: 'bg-amber-50', icon: 'bg-amber-100 text-amber-600', btn: 'bg-amber-600 hover:bg-amber-700', ring: 'ring-amber-100' };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onCancel}>
            <div className="bg-white rounded-2xl shadow-2xl w-[400px] max-w-[90vw] overflow-hidden animate-in" onClick={e => e.stopPropagation()}>
                <div className={`${colors.bg} px-6 py-5 flex flex-col items-center text-center`}>
                    <div className={`w-14 h-14 rounded-full ${colors.icon} flex items-center justify-center mb-3 ring-4 ${colors.ring}`}>
                        <FiAlertTriangle size={24} />
                    </div>
                    <h3 className="text-base font-bold text-gray-800">{title}</h3>
                    {userName && <p className="text-sm font-semibold text-gray-600 mt-1">{userName}</p>}
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">{message}</p>
                </div>
                <div className="flex gap-3 px-6 py-4 bg-white">
                    <button onClick={onCancel} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                        Cancelar
                    </button>
                    <button onClick={onConfirm} className={`flex-1 py-2.5 ${colors.btn} text-white rounded-xl text-sm font-medium transition-colors active:scale-[0.98]`}>
                        Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminSettings = () => {
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [activeSection, setActiveSection] = useState('users');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [confirmModal, setConfirmModal] = useState({ isOpen: false });

    const isAdmin = currentUser?.role === 'admin';
    const isSuperAdmin = currentUser?.email === ADMIN_EMAIL;
    const [workspaces, setWorkspaces] = useState([]);
    const [workspacesLoading, setWorkspacesLoading] = useState(false);

    useEffect(() => {
        if (!isSuperAdmin) return;
        setWorkspacesLoading(true);
        const unsub = subscribeToWorkspaces((ws) => {
            setWorkspaces(ws);
            setWorkspacesLoading(false);
        });
        return () => unsub();
    }, [isSuperAdmin]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(query(collection(db, 'users')));
            setUsers(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error("Error cargando usuarios:", error);
            toast.error("Error al cargar usuarios");
        }
        setLoading(false);
    };

    useEffect(() => { if (isAdmin) fetchUsers(); }, [isAdmin]);

    // Filtered users
    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            const matchSearch = !searchQuery ||
                (u.displayName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (u.email || '').toLowerCase().includes(searchQuery.toLowerCase());
            const matchStatus = statusFilter === 'ALL' || u.status === statusFilter;
            return matchSearch && matchStatus;
        });
    }, [users, searchQuery, statusFilter]);

    const admins = users.filter(u => u.role === 'admin');
    const activeUsers = users.filter(u => u.status === 'active');
    const pendingUsers = users.filter(u => u.status === 'pending');

    if (!isAdmin) {
        return (
            <div className="flex items-center justify-center h-full p-8">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="text-5xl mb-4">🚫</div>
                    <h1 className="text-xl font-bold text-gray-800 mb-2">Acceso Denegado</h1>
                    <p className="text-sm text-gray-500">Solo administradores pueden acceder a este panel.</p>
                </div>
            </div>
        );
    }

    // Handlers
    const updateUserStatus = async (userId, newStatus) => {
        setUpdatingId(userId);
        try {
            await updateDoc(doc(db, 'users', userId), { status: newStatus });
            if (newStatus === 'active') await toggleCollaborator(userId, true);
            setUsers(prev => prev.map(u => u.id === userId ? {
                ...u, status: newStatus,
                ...(newStatus === 'active' ? { isCollaborator: true, role: 'collaborator' } : {})
            } : u));
            toast.success(newStatus === 'active' ? 'Usuario aprobado' : 'Usuario rechazado');
        } catch (err) { toast.error(err.message || 'Error al actualizar'); }
        setUpdatingId(null);
    };

    const handleToggleCollaborator = async (userId, current) => {
        setUpdatingId(userId);
        try {
            await toggleCollaborator(userId, !current);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, isCollaborator: !current, role: !current ? 'collaborator' : 'user' } : u));
            toast.success(!current ? 'Colaborador habilitado' : 'Colaborador removido');
        } catch (err) { toast.error(err.message || 'Error al cambiar colaborador'); }
        setUpdatingId(null);
    };

    const handleToggleSeller = async (userId, current) => {
        setUpdatingId(userId);
        try {
            await toggleSeller(userId, !current);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, isSeller: !current } : u));
            toast.success(!current ? 'Vendedor habilitado' : 'Vendedor removido');
        } catch { toast.error('Error'); }
        setUpdatingId(null);
    };

    const handleToggleInstructor = async (userId, current) => {
        setUpdatingId(userId);
        try {
            await toggleInstructor(userId, !current);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, isInstructor: !current } : u));
            toast.success(!current ? 'Instructor habilitado' : 'Instructor removido');
        } catch { toast.error('Error'); }
        setUpdatingId(null);
    };

    const handleToggleAsesor = async (userId, current) => {
        setUpdatingId(userId);
        try {
            await updateDoc(doc(db, 'users', userId), { isAsesor: !current });
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, isAsesor: !current } : u));
            toast.success(!current ? 'Asesor habilitado' : 'Asesor removido');
        } catch { toast.error('Error'); }
        setUpdatingId(null);
    };

    const handleToggleAdmin = async (userId, currentRole) => {
        const targetUser = users.find(u => u.id === userId);
        if (targetUser?.email === ADMIN_EMAIL) { toast.error('No puedes modificar al super admin'); return; }
        const makeAdmin = currentRole !== 'admin';
        if (!window.confirm(makeAdmin
            ? `¿Promover a ${targetUser?.displayName || targetUser?.email} como administrador?`
            : `¿Revocar permisos de admin a ${targetUser?.displayName || targetUser?.email}?`
        )) return;
        setUpdatingId(userId);
        try {
            await toggleAdmin(userId, makeAdmin);
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: makeAdmin ? 'admin' : 'collaborator' } : u));
            toast.success(makeAdmin ? 'Admin otorgado' : 'Admin revocado');
        } catch { toast.error('Error'); }
        setUpdatingId(null);
    };

    const handleDeleteUser = (userId) => {
        const targetUser = users.find(u => u.id === userId);
        if (targetUser?.email === ADMIN_EMAIL) { toast.error('No puedes eliminar al super admin'); return; }
        if (targetUser?.role === 'admin') { toast.error('Revoca admin primero'); return; }
        setConfirmModal({
            isOpen: true,
            title: 'Eliminar Usuario',
            userName: targetUser?.displayName || targetUser?.email,
            message: 'Esta acción eliminará permanentemente al usuario y todos sus datos. No se puede deshacer.',
            type: 'danger',
            onConfirm: async () => {
                setConfirmModal({ isOpen: false });
                setUpdatingId(userId);
                try {
                    await deleteDoc(doc(db, 'users', userId));
                    setUsers(prev => prev.filter(u => u.id !== userId));
                    toast.success('Usuario eliminado');
                } catch { toast.error('Error al eliminar'); }
                setUpdatingId(null);
            }
        });
    };

    const handleUpdatePermissions = async (userId, permissions) => {
        setUpdatingId(userId);
        try {
            await updateDoc(doc(db, 'users', userId), { appPermissions: permissions });
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, appPermissions: permissions } : u));
            toast.success('Permisos actualizados');
        } catch { toast.error('Error al actualizar permisos'); }
        setUpdatingId(null);
    };

    const enableAllAsCollaborators = async () => {
        const targets = users.filter(u => u.status === 'active' && !u.isCollaborator && u.role !== 'admin');
        if (!targets.length) { toast('Todos ya son colaboradores'); return; }
        if (!window.confirm(`¿Habilitar ${targets.length} usuario(s) como colaboradores?`)) return;
        setUpdatingId('bulk');
        try {
            await Promise.all(targets.map(u => toggleCollaborator(u.id, true)));
            setUsers(prev => prev.map(u => targets.find(t => t.id === u.id) ? { ...u, isCollaborator: true, role: 'collaborator' } : u));
            toast.success(`${targets.length} usuario(s) habilitados`);
        } catch { toast.error('Error'); }
        setUpdatingId(null);
    };

    const pendingWorkspaces = workspaces.filter(w => w.status === 'pending');

    const sections = [
        ...(isSuperAdmin ? [{ id: 'workspaces', label: 'Empresas', icon: FiBriefcase, count: workspaces.length, badge: pendingWorkspaces.length }] : []),
        { id: 'users', label: 'Usuarios', icon: FiUsers, count: users.length },
        { id: 'admins', label: 'Administradores', icon: FiShield, count: admins.length },
        { id: 'security', label: 'Seguridad', icon: FiLock },
        { id: 'theme', label: 'Temas', icon: FiDroplet },
        { id: 'settings', label: 'Configuración', icon: FiSettings },
    ];

    // Plan badge for sidebar
    const wsPlan = workspaces.find(w => w.id === currentUser?.workspaceId);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <FiLoader className="animate-spin text-sky-500 mx-auto mb-3" size={32} />
                    <p className="text-sm text-gray-500">Cargando panel de admin...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full overflow-hidden">
            {/* Sidebar */}
            <aside className="w-56 bg-white border-r border-gray-100 shrink-0 flex flex-col">
                <div className="p-4 border-b border-gray-100">
                    <h1 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                        <FiShield className="text-sky-600" size={16} />
                        Admin Panel
                    </h1>
                    <p className="text-[10px] text-gray-400 mt-0.5">Gestión del sistema</p>
                </div>
                {isSuperAdmin && (
                    <div className="px-2 pt-2">
                        <button onClick={() => navigate('/super-admin')}
                            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 hover:from-indigo-100 hover:to-purple-100 transition-all border border-indigo-100">
                            <FiActivity size={14} />
                            Panel Super Admin
                        </button>
                    </div>
                )}
                <nav className="flex-1 p-2 space-y-0.5">
                    {sections.map(s => (
                        <button
                            key={s.id}
                            onClick={() => setActiveSection(s.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${activeSection === s.id
                                ? 'bg-sky-50 text-sky-700 shadow-sm'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                                }`}
                        >
                            <s.icon size={14} />
                            {s.label}
                            {s.badge > 0 && (
                                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-bold">{s.badge}</span>
                            )}
                            {s.count !== undefined && !s.badge && (
                                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${activeSection === s.id ? 'bg-sky-100 text-sky-600' : 'bg-gray-100 text-gray-500'
                                    }`}>{s.count}</span>
                            )}
                        </button>
                    ))}
                </nav>
                {/* Plan & Branding buttons */}
                <div className="px-2 pb-1 space-y-1">
                    <button onClick={() => navigate('/mi-plan')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-all border border-gray-100">
                        <FiStar size={13} className="text-amber-500" />
                        Mi Plan
                        {wsPlan && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-semibold capitalize">{wsPlan.plan || 'free'}</span>}
                    </button>
                    <button onClick={() => navigate('/mi-marca')}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-all border border-gray-100">
                        <FiDroplet size={13} className="text-purple-500" />
                        Mi Marca
                    </button>
                </div>
                {/* Stats */}
                <div className="p-3 border-t border-gray-100 space-y-1.5">
                    <div className="flex items-center justify-between text-[10px]">
                        <span className="text-gray-400">Activos</span>
                        <span className="font-bold text-emerald-600">{activeUsers.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px]">
                        <span className="text-gray-400">Pendientes</span>
                        <span className="font-bold text-amber-600">{pendingUsers.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px]">
                        <span className="text-gray-400">Admins</span>
                        <span className="font-bold text-sky-600">{admins.length}</span>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto p-6 bg-gray-50">
                {activeSection === 'workspaces' && isSuperAdmin && (
                    <WorkspacesSection
                        workspaces={workspaces}
                        loading={workspacesLoading}
                        updatingId={updatingId}
                        setUpdatingId={setUpdatingId}
                    />
                )}
                {activeSection === 'users' && (
                    <UsersSection
                        users={filteredUsers}
                        allUsers={users}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        updatingId={updatingId}
                        updateUserStatus={updateUserStatus}
                        handleToggleCollaborator={handleToggleCollaborator}
                        handleToggleSeller={handleToggleSeller}
                        handleToggleInstructor={handleToggleInstructor}
                        handleToggleAsesor={handleToggleAsesor}
                        handleToggleAdmin={handleToggleAdmin}
                        handleDeleteUser={handleDeleteUser}
                        handleUpdatePermissions={handleUpdatePermissions}
                        enableAllAsCollaborators={enableAllAsCollaborators}
                        fetchUsers={fetchUsers}
                    />
                )}
                {activeSection === 'admins' && (
                    <AdminsSection
                        users={users}
                        admins={admins}
                        updatingId={updatingId}
                        handleToggleAdmin={handleToggleAdmin}
                    />
                )}
                {activeSection === 'security' && <SecuritySection />}
                {activeSection === 'theme' && <ThemeSection />}
                {activeSection === 'settings' && <SettingsSection isSuperAdmin={isSuperAdmin} currentUser={currentUser} />}
            </main>

            {/* Confirm Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                userName={confirmModal.userName}
                type={confirmModal.type}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal({ isOpen: false })}
            />
        </div>
    );
};

// ═══════════════════════════════════════
// SECTION: USERS
// ═══════════════════════════════════════
const UsersSection = ({ users, allUsers, searchQuery, setSearchQuery, statusFilter, setStatusFilter,
    updatingId, updateUserStatus, handleToggleCollaborator, handleToggleSeller, handleToggleInstructor, handleToggleAsesor, handleToggleAdmin, handleDeleteUser,
    handleUpdatePermissions, enableAllAsCollaborators, fetchUsers }) => {

    const [expandedUser, setExpandedUser] = useState(null);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-bold text-gray-800">Gestión de Usuarios</h2>
                    <p className="text-xs text-gray-500">{allUsers.length} usuarios registrados</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={fetchUsers} className="p-2 rounded-lg bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors" title="Refrescar">
                        <FiRefreshCw size={14} />
                    </button>
                    <button onClick={enableAllAsCollaborators} disabled={updatingId === 'bulk'}
                        className="px-3 py-2 bg-sky-600 text-white text-xs font-medium rounded-lg hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors">
                        {updatingId === 'bulk' ? <FiLoader className="animate-spin" size={12} /> : <FiUsers size={12} />}
                        Activar Todos
                    </button>
                </div>
            </div>

            {/* Search & Filter */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Buscar por nombre o email..."
                        className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-200 focus:border-sky-400"
                    />
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    className="text-xs bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <option value="ALL">Todos los estados</option>
                    <option value="active">Activos</option>
                    <option value="pending">Pendientes</option>
                    <option value="rejected">Rechazados</option>
                </select>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead>
                            <tr className="bg-gray-50 text-gray-500 border-b text-[11px] uppercase">
                                <th className="px-4 py-2.5 font-semibold">Usuario</th>
                                <th className="px-4 py-2.5 font-semibold">Estado</th>
                                <th className="px-4 py-2.5 font-semibold">Rol</th>
                                <th className="px-4 py-2.5 font-semibold text-center">Colaborador</th>
                                <th className="px-4 py-2.5 font-semibold text-center">Vendedor</th>
                                <th className="px-4 py-2.5 font-semibold text-center">Instructor</th>
                                <th className="px-4 py-2.5 font-semibold text-center">Asesor</th>
                                <th className="px-4 py-2.5 font-semibold text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {users.length === 0 ? (
                                <tr><td colSpan={7} className="text-center py-8 text-gray-400 text-xs italic">No se encontraron usuarios</td></tr>
                            ) : users.map(u => (
                                <React.Fragment key={u.id}>
                                    <tr className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-4 py-2.5">
                                            <button onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)} className="flex items-center gap-3 text-left">
                                                {u.photoURL ? (
                                                    <img src={u.photoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                                                        {(u.displayName || u.email || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-xs font-semibold text-gray-800">{u.displayName || 'Sin nombre'}</p>
                                                    <p className="text-[10px] text-gray-400">{u.email}</p>
                                                </div>
                                                <FiChevronDown size={12} className={`text-gray-300 transition-transform ${expandedUser === u.id ? 'rotate-180' : ''}`} />
                                            </button>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <StatusBadge status={u.status} />
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <RoleBadge role={u.role} email={u.email} />
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            {u.role === 'admin' ? (
                                                <span className="text-[10px] text-gray-400">—</span>
                                            ) : u.status === 'active' ? (
                                                <ToggleSwitch
                                                    enabled={u.isCollaborator}
                                                    loading={updatingId === u.id}
                                                    onToggle={() => handleToggleCollaborator(u.id, u.isCollaborator)}
                                                    color="sky"
                                                />
                                            ) : <span className="text-[10px] text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            {u.role === 'admin' ? (
                                                <span className="text-[10px] text-gray-400">—</span>
                                            ) : u.status === 'active' ? (
                                                <ToggleSwitch
                                                    enabled={u.isSeller}
                                                    loading={updatingId === u.id}
                                                    onToggle={() => handleToggleSeller(u.id, u.isSeller)}
                                                    color="emerald"
                                                />
                                            ) : <span className="text-[10px] text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            {u.role === 'admin' ? (
                                                <span className="text-[10px] text-gray-400">—</span>
                                            ) : u.status === 'active' ? (
                                                <ToggleSwitch
                                                    enabled={u.isInstructor}
                                                    loading={updatingId === u.id}
                                                    onToggle={() => handleToggleInstructor(u.id, u.isInstructor)}
                                                    color="purple"
                                                />
                                            ) : <span className="text-[10px] text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            {u.role === 'admin' || u.status === 'active' ? (
                                                <ToggleSwitch
                                                    enabled={u.isAsesor}
                                                    loading={updatingId === u.id}
                                                    onToggle={() => handleToggleAsesor(u.id, u.isAsesor)}
                                                    color="emerald"
                                                />
                                            ) : <span className="text-[10px] text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                {u.role !== 'admin' && u.status !== 'active' && (
                                                    <button onClick={() => updateUserStatus(u.id, 'active')} disabled={updatingId === u.id}
                                                        className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50 transition-colors" title="Aprobar">
                                                        {updatingId === u.id ? <FiLoader className="animate-spin" size={12} /> : <FiUserCheck size={12} />}
                                                    </button>
                                                )}
                                                {u.role !== 'admin' && u.status !== 'rejected' && (
                                                    <button onClick={() => updateUserStatus(u.id, 'rejected')} disabled={updatingId === u.id}
                                                        className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors" title="Rechazar">
                                                        {updatingId === u.id ? <FiLoader className="animate-spin" size={12} /> : <FiUserX size={12} />}
                                                    </button>
                                                )}
                                                {u.email !== ADMIN_EMAIL && (
                                                    <button onClick={() => handleDeleteUser(u.id)} disabled={updatingId === u.id}
                                                        className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-50 transition-colors" title="Eliminar">
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {/* Expanded Row */}
                                    {expandedUser === u.id && (
                                        <tr>
                                            <td colSpan={7} className="bg-gray-50/50 px-6 py-3">
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] mb-4">
                                                    <div>
                                                        <span className="text-gray-400 block">ID</span>
                                                        <span className="text-gray-700 font-mono text-[10px]">{u.id}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 block">Workspace</span>
                                                        <span className="text-gray-700 font-mono text-[10px]">{u.workspaceId || '—'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 block">Registro</span>
                                                        <span className="text-gray-700">{u.createdAt?.toDate ? format(u.createdAt.toDate(), 'dd MMM yyyy', { locale: es }) : '—'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-400 block">Última actualización</span>
                                                        <span className="text-gray-700">{u.updatedAt?.toDate ? format(u.updatedAt.toDate(), 'dd MMM yyyy HH:mm', { locale: es }) : '—'}</span>
                                                    </div>
                                                </div>
                                                {/* App Permissions */}
                                                {u.role !== 'admin' && (
                                                    <div className="border-t border-gray-200 pt-3">
                                                        <p className="text-[11px] font-bold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
                                                            <FiShield size={11} /> Permisos de Aplicaciones
                                                        </p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {APP_MODULES.filter(mod => !mod.hidden).map(mod => {
                                                                const perms = u.appPermissions || {};
                                                                // opt-in: encendido solo si === true; el resto: encendido salvo === false
                                                                const enabled = mod.optIn ? perms[mod.key] === true : perms[mod.key] !== false;
                                                                return (
                                                                    <button key={mod.key}
                                                                        onClick={() => {
                                                                            const newPerms = { ...(u.appPermissions || {}) };
                                                                            newPerms[mod.key] = !enabled;
                                                                            handleUpdatePermissions(u.id, newPerms);
                                                                        }}
                                                                        disabled={updatingId === u.id}
                                                                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${enabled
                                                                            ? 'bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100'
                                                                            : 'bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100'
                                                                            } disabled:opacity-50`}
                                                                    >
                                                                        <span>{mod.icon}</span>
                                                                        {mod.label}
                                                                        <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] ${enabled ? 'bg-sky-500 text-white' : 'bg-gray-300 text-white'
                                                                            }`}>
                                                                            {enabled ? '✓' : '✕'}
                                                                        </span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════
// SECTION: ADMINISTRATORS
// ═══════════════════════════════════════
const AdminsSection = ({ users, admins, updatingId, handleToggleAdmin }) => {
    const nonAdmins = users.filter(u => u.role !== 'admin' && u.status === 'active');

    return (
        <div className="space-y-5">
            <div>
                <h2 className="text-lg font-bold text-gray-800">Administradores</h2>
                <p className="text-xs text-gray-500">Gestiona quién tiene acceso de administrador al sistema</p>
            </div>

            {/* Current Admins */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-xs font-bold text-gray-400 uppercase mb-3 flex items-center gap-2">
                    <FiShield size={12} /> Administradores Actuales ({admins.length})
                </h3>
                <div className="space-y-2">
                    {admins.map(admin => (
                        <div key={admin.id} className="flex items-center justify-between p-3 rounded-lg bg-sky-50/50 border border-sky-100">
                            <div className="flex items-center gap-3">
                                {admin.photoURL ? (
                                    <img src={admin.photoURL} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-sky-200" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sm font-bold text-sky-600">
                                        {(admin.displayName || '?').charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm font-semibold text-gray-800">{admin.displayName || 'Sin nombre'}</p>
                                    <p className="text-[10px] text-gray-500">{admin.email}</p>
                                </div>
                                {admin.email === ADMIN_EMAIL && (
                                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center gap-1">
                                        <FiStar size={10} /> Super Admin
                                    </span>
                                )}
                            </div>
                            {admin.email !== ADMIN_EMAIL && (
                                <button
                                    onClick={() => handleToggleAdmin(admin.id, admin.role)}
                                    disabled={updatingId === admin.id}
                                    className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                                >
                                    {updatingId === admin.id ? <FiLoader className="animate-spin" size={12} /> : <FiX size={12} />}
                                    Revocar Admin
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Promote to Admin */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-xs font-bold text-gray-400 uppercase mb-3 flex items-center gap-2">
                    <FiUserCheck size={12} /> Promover a Administrador
                </h3>
                <p className="text-[11px] text-gray-500 mb-3">
                    Selecciona un usuario activo para otorgarle permisos de administrador. Los admins pueden gestionar usuarios, configuraciones y tienen acceso completo al sistema.
                </p>
                {nonAdmins.length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-4 text-center">No hay usuarios activos para promover</p>
                ) : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {nonAdmins.map(u => (
                            <div key={u.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                                <div className="flex items-center gap-3">
                                    {u.photoURL ? (
                                        <img src={u.photoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                                            {(u.displayName || '?').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-xs font-medium text-gray-800">{u.displayName || 'Sin nombre'}</p>
                                        <p className="text-[10px] text-gray-400">{u.email}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleToggleAdmin(u.id, u.role)}
                                    disabled={updatingId === u.id}
                                    className="px-3 py-1.5 rounded-lg bg-sky-50 text-sky-600 text-xs font-medium hover:bg-sky-100 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                                >
                                    {updatingId === u.id ? <FiLoader className="animate-spin" size={12} /> : <FiShield size={12} />}
                                    Hacer Admin
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Permissions Info */}
            <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
                <div className="flex gap-3">
                    <FiAlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={16} />
                    <div className="text-xs text-amber-700 space-y-1">
                        <p className="font-semibold">Permisos de Administrador</p>
                        <ul className="space-y-0.5 text-amber-600 list-disc list-inside">
                            <li>Acceso completo al panel de administración</li>
                            <li>Gestionar usuarios, roles y permisos</li>
                            <li>Ver y editar todos los registros de asistencia y horas extra</li>
                            <li>Configurar horarios y tarifas de empleados</li>
                            <li>Acceso a reportes y exportación de datos</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════
// SECTION: SECURITY
// ═══════════════════════════════════════
const SecuritySection = () => (
    <div className="space-y-5">
        <div>
            <h2 className="text-lg font-bold text-gray-800">Seguridad</h2>
            <p className="text-xs text-gray-500">Configuraciones de seguridad y acceso</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoCard
                icon={FiLock}
                title="Autenticación"
                description="Google OAuth 2.0"
                detail="Los usuarios inician sesión con su cuenta de Google. No se almacenan contraseñas."
                color="sky"
            />
            <InfoCard
                icon={FiShield}
                title="Autorización"
                description="Basada en roles"
                detail="Roles: Super Admin, Admin, Colaborador, Vendedor, Usuario."
                color="emerald"
            />
            <InfoCard
                icon={FiDatabase}
                title="Base de Datos"
                description="Firebase Firestore"
                detail="Datos encriptados en tránsito y en reposo. Reglas de seguridad configuradas."
                color="amber"
            />
            <InfoCard
                icon={FiActivity}
                title="Hosting"
                description="Firebase Hosting"
                detail="HTTPS forzado. CDN global. Certificado SSL automático."
                color="purple"
            />
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase mb-3">Políticas de Acceso</h3>
            <div className="space-y-3">
                <PolicyRow label="Registro de nuevos usuarios" value="Requiere aprobación de admin" />
                <PolicyRow label="Acceso a datos de otros usuarios" value="Solo administradores" />
                <PolicyRow label="Registro de asistencia" value="Solo en horario laboral (±2hrs)" />
                <PolicyRow label="Eliminación de registros" value="Solo administradores" />
                <PolicyRow label="Exportación de datos" value="Solo administradores" />
                <PolicyRow label="Configuración de tarifas" value="Solo administradores" />
            </div>
        </div>
    </div>
);

// ═══════════════════════════════════════
// SECTION: SETTINGS
// ═══════════════════════════════════════
// ═══════════════════════════════════════
// SECTION: THEMES
// ═══════════════════════════════════════
const ThemeSection = () => {
    const { sectionColors, updateSectionColor, applyGlobal, globalColor, updateGlobalSettings } = useTheme();

    return (
        <div className="space-y-5">
            <div>
                <h2 className="text-lg font-bold text-gray-800">Personalizar Temas</h2>
                <p className="text-xs text-gray-500">Configura los colores de cada sección de la aplicación</p>
            </div>

            {/* Global Toggle */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-sm font-bold text-gray-700">Aplicar a todo el sitio</h3>
                        <p className="text-[10px] text-gray-400 mt-0.5">Un solo color para todas las secciones</p>
                    </div>
                    <button
                        onClick={() => updateGlobalSettings(!applyGlobal, globalColor)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${applyGlobal ? 'bg-sky-500' : 'bg-gray-200'}`}
                    >
                        <span className={`${applyGlobal ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm`} />
                    </button>
                </div>

                {applyGlobal && (
                    <div>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase mb-2">Color global</p>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
                                <button
                                    key={key}
                                    onClick={() => updateGlobalSettings(true, key)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-xs font-medium ${globalColor === key ? 'border-gray-800 shadow-md scale-105' : 'border-gray-100 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="w-5 h-5 rounded-full shadow-inner" style={{ backgroundColor: preset.primary }} />
                                    {preset.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Per-section colors */}
            {!applyGlobal && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-xs font-bold text-gray-400 uppercase mb-4">Colores por sección</h3>
                    <div className="space-y-4">
                        {Object.entries(SECTIONS).map(([sectionKey, section]) => (
                            <div key={sectionKey} className="border border-gray-50 rounded-xl p-4 hover:bg-gray-50/50 transition">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-base">{section.icon}</span>
                                    <span className="text-sm font-bold text-gray-700">{section.label}</span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium ml-auto" style={{
                                        backgroundColor: COLOR_PRESETS[sectionColors[sectionKey]]?.light || '#e0f2fe',
                                        color: COLOR_PRESETS[sectionColors[sectionKey]]?.text || '#0c4a6e',
                                    }}>
                                        {COLOR_PRESETS[sectionColors[sectionKey]]?.name || 'Azul'}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
                                        <button
                                            key={key}
                                            onClick={() => updateSectionColor(sectionKey, key)}
                                            className={`w-8 h-8 rounded-lg border-2 transition-all flex items-center justify-center ${sectionColors[sectionKey] === key
                                                ? 'border-gray-800 scale-110 shadow-lg'
                                                : 'border-transparent hover:border-gray-300 hover:scale-105'
                                                }`}
                                            style={{ backgroundColor: preset.primary }}
                                            title={preset.name}
                                        >
                                            {sectionColors[sectionKey] === key && (
                                                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Preview */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <h3 className="text-xs font-bold text-gray-400 uppercase mb-4">Vista previa</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(SECTIONS).map(([sectionKey, section]) => {
                        const colorKey = applyGlobal ? globalColor : (sectionColors[sectionKey] || 'blue');
                        const preset = COLOR_PRESETS[colorKey];
                        return (
                            <div key={sectionKey} className="rounded-xl p-3 border" style={{ borderColor: preset.light, backgroundColor: preset.lighter }}>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-sm" style={{ backgroundColor: preset.primary }}>
                                        {section.icon}
                                    </div>
                                    <span className="text-xs font-bold" style={{ color: preset.text }}>{section.label}</span>
                                </div>
                                <div className="flex gap-1.5">
                                    <button className="text-[10px] px-2.5 py-1 rounded-lg text-white font-medium" style={{ backgroundColor: preset.primary }}>
                                        Botón
                                    </button>
                                    <button className="text-[10px] px-2.5 py-1 rounded-lg font-medium" style={{ backgroundColor: preset.light, color: preset.text }}>
                                        Secundario
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const ApiKeyCard = ({ currentUser }) => {
    const [meta, setMeta] = useState(null);
    const [input, setInput] = useState('');
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null); // null | { ok: bool, msg: string }

    useEffect(() => {
        const unsub = subscribeToGeminiKeyMeta((data) => {
            setMeta(data);
            setInput(data?.apiKey || '');
        });
        return () => unsub();
    }, []);

    const dirty = input.trim() !== (meta?.apiKey || '');
    const source = meta?.apiKey ? 'firestore' : (process.env.REACT_APP_GEMINI_API_KEY ? 'env' : 'none');
    const sourceLabel = {
        firestore: { text: 'Firestore', color: 'bg-emerald-100 text-emerald-700' },
        env: { text: 'Fallback (.env)', color: 'bg-gray-100 text-gray-600' },
        none: { text: 'Sin configurar', color: 'bg-red-100 text-red-700' },
    }[source];

    const handleSave = async () => {
        if (!input.trim()) {
            toast.error('La clave no puede estar vacía');
            return;
        }
        setSaving(true);
        try {
            await saveGeminiKey(input.trim(), currentUser?.email);
            toast.success('Clave guardada correctamente');
            setTestResult(null);
        } catch (e) {
            toast.error('Error al guardar: ' + (e?.message || 'desconocido'));
        }
        setSaving(false);
    };

    const handleTest = async () => {
        if (!input.trim()) return;
        setTesting(true);
        setTestResult(null);
        try {
            const original = (meta?.apiKey || '');
            // Guardamos temporalmente la clave del input para que callGemini la use,
            // pero solo si ya cambió respecto a la guardada. Si no, usamos la actual.
            if (input.trim() !== original) {
                // Sin guardar todavía: probamos directo contra Google con la del input.
                const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${input.trim()}`;
                const res = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [{ parts: [{ text: 'ping' }] }],
                        generationConfig: { maxOutputTokens: 10 },
                    }),
                });
                if (res.ok) {
                    setTestResult({ ok: true, msg: 'Clave válida' });
                } else {
                    const err = await res.json().catch(() => ({}));
                    setTestResult({ ok: false, msg: err?.error?.message || `Error ${res.status}` });
                }
            } else {
                await callGemini('ping', 'Responde solo con "ok".');
                setTestResult({ ok: true, msg: 'Clave válida' });
            }
        } catch (e) {
            setTestResult({ ok: false, msg: e?.message || 'Error al probar' });
        }
        setTesting(false);
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <FiKey className="text-indigo-500" size={16} />
                    <h3 className="text-xs font-bold text-gray-400 uppercase">API Keys del Sistema</h3>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${sourceLabel.color}`}>
                    {sourceLabel.text}
                </span>
            </div>

            <div className="space-y-3">
                <label className="block">
                    <span className="text-xs font-semibold text-gray-700">Gemini API Key</span>
                    <p className="text-[10px] text-gray-400 mb-2">
                        Obtén tu clave en{' '}
                        <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">aistudio.google.com/app/apikey</a>
                    </p>
                    <div className="relative">
                        <input
                            type={show ? 'text' : 'password'}
                            value={input}
                            onChange={(e) => { setInput(e.target.value); setTestResult(null); }}
                            placeholder="AIzaSy..."
                            className="w-full px-3 py-2 pr-20 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 font-mono"
                        />
                        <button
                            type="button"
                            onClick={() => setShow(!show)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                            title={show ? 'Ocultar' : 'Mostrar'}
                        >
                            {show ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                        </button>
                    </div>
                </label>

                {testResult && (
                    <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                        {testResult.ok ? <FiCheck size={14} /> : <FiXCircle size={14} />}
                        <span>{testResult.msg}</span>
                    </div>
                )}

                <div className="flex items-center justify-between pt-2">
                    <p className="text-[10px] text-gray-400">
                        {meta?.updatedAt
                            ? `Última actualización: ${format(meta.updatedAt.toDate(), "d 'de' MMM yyyy, HH:mm", { locale: es })} · ${meta.updatedBy || 'desconocido'}`
                            : 'Sin configurar — usando clave del build (.env)'}
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={handleTest}
                            disabled={testing || !input.trim()}
                            className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                            {testing ? <FiLoader size={12} className="animate-spin" /> : <FiActivity size={12} />}
                            Probar
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!dirty || saving}
                            className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                            {saving ? <FiLoader size={12} className="animate-spin" /> : <FiSave size={12} />}
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SettingsSection = ({ isSuperAdmin, currentUser }) => (
    <div className="space-y-5">
        <div>
            <h2 className="text-lg font-bold text-gray-800">Configuración General</h2>
            <p className="text-xs text-gray-500">Ajustes generales del sistema</p>
        </div>

        {isSuperAdmin && <ApiKeyCard currentUser={currentUser} />}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase mb-4">Información del Sistema</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SettingRow icon={FiDatabase} label="Proyecto Firebase" value="gestor-de-respuestas-hingenia" />
                <SettingRow icon={FiActivity} label="URL del Sitio" value="gestor-de-respuestas-hingenia.web.app" />
                <SettingRow icon={FiCalendar} label="Versión" value="2.0.0" />
                <SettingRow icon={FiClock} label="Zona Horaria" value="America/Lima (UTC-5)" />
                <SettingRow icon={FiMail} label="Admin Principal" value={ADMIN_EMAIL} />
                <SettingRow icon={FiShield} label="Dark Mode" value="Habilitado (toggle en header)" />
            </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-bold text-gray-400 uppercase mb-4">Módulos del Sistema</h3>
            <div className="space-y-2">
                <ModuleRow name="Tareas" desc="Gestión de proyectos, listas y tareas" enabled />
                <ModuleRow name="Calendario" desc="Programación académica y horarios" enabled />
                <ModuleRow name="Horas Extra" desc="Control de asistencia y horas extras" enabled />
                <ModuleRow name="Ventas" desc="Módulo de ventas para asesores" enabled />
            </div>
        </div>
    </div>
);

// ═══════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════
const StatusBadge = ({ status }) => {
    const styles = {
        active: 'bg-emerald-100 text-emerald-700',
        pending: 'bg-amber-100 text-amber-700',
        rejected: 'bg-red-100 text-red-700',
    };
    const labels = { active: 'Activo', pending: 'Pendiente', rejected: 'Rechazado' };
    return (
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${styles[status] || 'bg-gray-100 text-gray-500'}`}>
            {labels[status] || status}
        </span>
    );
};

const RoleBadge = ({ role, email }) => {
    if (role === 'admin') {
        return (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-sky-100 text-sky-700 flex items-center gap-1 w-fit">
                <FiShield size={10} />
                {email === ADMIN_EMAIL ? 'Super Admin' : 'Admin'}
            </span>
        );
    }
    if (role === 'collaborator') {
        return <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-700">Colaborador</span>;
    }
    return <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-500">Usuario</span>;
};

const ToggleSwitch = ({ enabled, loading, onToggle, color = 'sky' }) => (
    <button
        onClick={onToggle}
        disabled={loading}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${enabled ? (color === 'emerald' ? 'bg-emerald-500' : color === 'purple' ? 'bg-purple-500' : 'bg-sky-500') : 'bg-gray-200'
            }`}
    >
        <span className={`${enabled ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 rounded-full bg-white transition-transform shadow-sm`} />
    </button>
);

const InfoCard = ({ icon: Icon, title, description, detail, color }) => {
    const colors = {
        sky: 'bg-sky-50 text-sky-600 border-sky-100',
        emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
        amber: 'bg-amber-50 text-amber-600 border-amber-100',
        purple: 'bg-purple-50 text-purple-600 border-purple-100',
    };
    return (
        <div className={`rounded-xl border p-4 ${colors[color]}`}>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={16} />
                <span className="text-sm font-bold">{title}</span>
            </div>
            <p className="text-xs font-semibold opacity-80">{description}</p>
            <p className="text-[10px] opacity-60 mt-1">{detail}</p>
        </div>
    );
};

const PolicyRow = ({ label, value }) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
        <span className="text-xs text-gray-600">{label}</span>
        <span className="text-xs font-medium text-gray-800">{value}</span>
    </div>
);

const SettingRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
        <Icon className="text-gray-400 shrink-0" size={14} />
        <div>
            <p className="text-[10px] text-gray-400">{label}</p>
            <p className="text-xs font-medium text-gray-700">{value}</p>
        </div>
    </div>
);

const ModuleRow = ({ name, desc, enabled }) => (
    <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
        <div>
            <p className="text-xs font-semibold text-gray-800">{name}</p>
            <p className="text-[10px] text-gray-400">{desc}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
            {enabled ? 'Activo' : 'Inactivo'}
        </span>
    </div>
);

// ═══════════════════════════════════════
// SECTION: WORKSPACES (Super-Admin)
// ═══════════════════════════════════════
const WorkspacesSection = ({ workspaces, loading, updatingId, setUpdatingId }) => {
    const [filter, setFilter] = useState('ALL');
    const [search, setSearch] = useState('');
    const [expandedWs, setExpandedWs] = useState(null);

    const filtered = workspaces.filter(w => {
        const matchFilter = filter === 'ALL' || w.status === filter;
        const matchSearch = !search ||
            (w.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (w.ownerEmail || '').toLowerCase().includes(search.toLowerCase()) ||
            (w.ruc || '').includes(search);
        return matchFilter && matchSearch;
    });

    const statusCounts = {
        pending: workspaces.filter(w => w.status === 'pending').length,
        active: workspaces.filter(w => w.status === 'active').length,
        suspended: workspaces.filter(w => w.status === 'suspended').length,
        rejected: workspaces.filter(w => w.status === 'rejected').length,
    };

    const handleApprove = async (wsId) => {
        setUpdatingId(wsId);
        try {
            await approveWorkspace(wsId);
            toast.success('Empresa aprobada');
        } catch { toast.error('Error al aprobar'); }
        setUpdatingId(null);
    };

    const handleReject = async (wsId) => {
        if (!window.confirm('¿Rechazar esta empresa?')) return;
        setUpdatingId(wsId);
        try {
            await rejectWorkspace(wsId);
            toast.success('Empresa rechazada');
        } catch { toast.error('Error al rechazar'); }
        setUpdatingId(null);
    };

    const handleSuspend = async (wsId) => {
        if (!window.confirm('¿Suspender esta empresa? Sus usuarios no podran acceder.')) return;
        setUpdatingId(wsId);
        try {
            await suspendWorkspace(wsId);
            toast.success('Empresa suspendida');
        } catch { toast.error('Error al suspender'); }
        setUpdatingId(null);
    };

    const handleReactivate = async (wsId) => {
        setUpdatingId(wsId);
        try {
            await approveWorkspace(wsId);
            toast.success('Empresa reactivada');
        } catch { toast.error('Error'); }
        setUpdatingId(null);
    };

    const WsStatusBadge = ({ status }) => {
        const styles = {
            pending: 'bg-amber-50 text-amber-700 border-amber-200',
            active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
            suspended: 'bg-gray-100 text-gray-600 border-gray-200',
            rejected: 'bg-red-50 text-red-700 border-red-200',
        };
        const labels = { pending: 'Pendiente', active: 'Activa', suspended: 'Suspendida', rejected: 'Rechazada' };
        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${styles[status] || styles.pending}`}>
                {labels[status] || status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <FiLoader className="animate-spin text-sky-500" size={24} />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-bold text-gray-800">Empresas Registradas</h2>
                <p className="text-xs text-gray-500">{workspaces.length} empresas en el sistema</p>
            </div>

            {/* Status cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Pendientes', count: statusCounts.pending, color: 'amber', icon: FiClock },
                    { label: 'Activas', count: statusCounts.active, color: 'emerald', icon: FiCheck },
                    { label: 'Suspendidas', count: statusCounts.suspended, color: 'gray', icon: FiPause },
                    { label: 'Rechazadas', count: statusCounts.rejected, color: 'red', icon: FiXCircle },
                ].map(card => (
                    <div key={card.label} className={`bg-white rounded-xl border border-gray-100 p-4 shadow-sm`}>
                        <div className="flex items-center gap-2 mb-1">
                            <card.icon size={14} className={`text-${card.color}-500`} />
                            <span className="text-[11px] text-gray-500">{card.label}</span>
                        </div>
                        <span className={`text-2xl font-bold text-${card.color}-600`}>{card.count}</span>
                    </div>
                ))}
            </div>

            {/* Search & Filter */}
            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar por nombre, email o RUC..."
                        className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-200 focus:border-sky-400"
                    />
                </div>
                <select value={filter} onChange={e => setFilter(e.target.value)}
                    className="text-xs bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <option value="ALL">Todos</option>
                    <option value="pending">Pendientes</option>
                    <option value="active">Activas</option>
                    <option value="suspended">Suspendidas</option>
                    <option value="rejected">Rechazadas</option>
                </select>
            </div>

            {/* Workspaces list */}
            <div className="space-y-3">
                {filtered.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                        <FiBriefcase size={32} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-sm text-gray-400">No se encontraron empresas</p>
                    </div>
                ) : filtered.map(ws => (
                    <div key={ws.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${ws.status === 'pending' ? 'border-amber-200 ring-1 ring-amber-100' : 'border-gray-100'}`}>
                        <div className="flex items-center gap-4 px-5 py-4">
                            {/* Logo/Avatar */}
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-lg font-bold text-indigo-600 flex-shrink-0">
                                {(ws.name || '?').charAt(0).toUpperCase()}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-bold text-gray-800 truncate">{ws.name}</h3>
                                    <WsStatusBadge status={ws.status} />
                                </div>
                                <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400">
                                    <span className="flex items-center gap-1"><FiMail size={10} />{ws.ownerEmail}</span>
                                    {ws.country && <span className="flex items-center gap-1"><FiGlobe size={10} />{ws.country}</span>}
                                    {ws.phone && <span className="flex items-center gap-1"><FiPhone size={10} />{ws.phone}</span>}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                {ws.status === 'pending' && (
                                    <>
                                        <button onClick={() => handleApprove(ws.id)} disabled={updatingId === ws.id}
                                            className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1 transition-colors">
                                            {updatingId === ws.id ? <FiLoader className="animate-spin" size={12} /> : <FiCheck size={12} />}
                                            Aprobar
                                        </button>
                                        <button onClick={() => handleReject(ws.id)} disabled={updatingId === ws.id}
                                            className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center gap-1 transition-colors">
                                            <FiXCircle size={12} /> Rechazar
                                        </button>
                                    </>
                                )}
                                {ws.status === 'active' && (
                                    <button onClick={() => handleSuspend(ws.id)} disabled={updatingId === ws.id}
                                        className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1 transition-colors">
                                        <FiPause size={12} /> Suspender
                                    </button>
                                )}
                                {(ws.status === 'suspended' || ws.status === 'rejected') && (
                                    <button onClick={() => handleReactivate(ws.id)} disabled={updatingId === ws.id}
                                        className="px-3 py-1.5 bg-emerald-50 text-emerald-600 text-xs font-medium rounded-lg hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1 transition-colors">
                                        <FiCheck size={12} /> Reactivar
                                    </button>
                                )}
                                <button onClick={() => setExpandedWs(expandedWs === ws.id ? null : ws.id)}
                                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
                                    <FiChevronDown size={14} className={`transition-transform ${expandedWs === ws.id ? 'rotate-180' : ''}`} />
                                </button>
                            </div>
                        </div>

                        {/* Expanded details */}
                        {expandedWs === ws.id && (
                            <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px]">
                                    <div>
                                        <span className="text-gray-400 block">RUC / NIT</span>
                                        <span className="text-gray-700 font-medium">{ws.ruc || 'No especificado'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">Industria</span>
                                        <span className="text-gray-700 font-medium">{ws.industry || '—'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">Tamano</span>
                                        <span className="text-gray-700 font-medium">{ws.size || '—'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">Plan</span>
                                        <span className="text-gray-700 font-medium capitalize">{ws.plan || 'free'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">Miembros</span>
                                        <span className="text-gray-700 font-medium">{ws.memberCount || 1}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">Onboarding</span>
                                        <span className={`font-medium ${ws.onboardingCompleted ? 'text-emerald-600' : 'text-amber-600'}`}>
                                            {ws.onboardingCompleted ? 'Completado' : 'Pendiente'}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">Registro</span>
                                        <span className="text-gray-700 font-medium">
                                            {ws.createdAt?.toDate ? format(ws.createdAt.toDate(), 'dd MMM yyyy HH:mm', { locale: es }) : '—'}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400 block">Workspace ID</span>
                                        <span className="text-gray-500 font-mono text-[10px]">{ws.id}</span>
                                    </div>
                                </div>

                                {/* Modules */}
                                {ws.modules && (
                                    <div className="mt-4">
                                        <span className="text-gray-400 text-[11px] block mb-2">Modulos activos</span>
                                        <div className="flex flex-wrap gap-1.5">
                                            {Object.entries(ws.modules).filter(([k, v]) => v && !APP_MODULES.find(m => m.key === k)?.hidden).map(([key]) => {
                                                const mod = APP_MODULES.find(m => m.key === key);
                                                return (
                                                    <span key={key} className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-lg border border-gray-200 text-[10px] font-medium text-gray-600">
                                                        {mod?.icon || '📦'} {mod?.label || key}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminSettings;
